// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the legend of the change from a held baseline (research tier):
 * the diverging bar with its ends and zero, in the dataset's units or in
 * percent. One builder for the Analyze panel and the legend floating on
 * the globe, so the two never disagree about what a colour means.
 */

import { changeBarStops, type ChangeColours, type ChangeMode } from '../services/research/changeMap'
import { formatStatValue } from './analyzeCharts'
import { formatNumber } from '../i18n/format'
import { t } from '../i18n'

export interface ChangeLegendOptions {
  readonly mode: ChangeMode
  /** The fixed colour range: ± limit (the + end when `min` is given). */
  readonly limit: number
  /** AYNI: the − end of the viewer's own range; −limit when not given. */
  readonly min?: number
  /** AYNI (2026-10-20): the palette the viewer chose; RdBu when not given. */
  readonly colours?: ChangeColours
  readonly units?: string
  /** The frame the baseline was held at, for the title. */
  readonly frameLabel?: string | null
  /** AYNI: a title of its own (Compare: "A − B"), instead of the baseline's. */
  readonly title?: string
}

/** "+1 °C", "−1,000 %", "0 °C"; AYNI: "+3 σ" for a standardised change. */
export function changeTick(value: number, mode: ChangeMode, units?: string): string {
  const fmt = (v: number) => (mode === 'percent'
    ? `${formatNumber(v, { maximumSignificantDigits: 3 })} %`
    : mode === 'sigma'
      ? `${formatNumber(v, { maximumSignificantDigits: 2 })} σ` // i18n-exempt: a number and the σ symbol
      : formatStatValue(v, units))
  return value > 0 ? `+${fmt(value)}` : value < 0 ? `−${fmt(-value)}` : fmt(0)
}

/** AYNI: the change palette as a CSS gradient, − end first. */
export function changeGradient(colours?: ChangeColours, direction = 'to right'): string {
  return `linear-gradient(${direction}, ${changeBarStops(colours).map((c) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`).join(', ')})`
}

/** The title, bar and ticks. */
export function buildChangeLegend(opts: ChangeLegendOptions): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'change-legend'
  const title = document.createElement('p')
  title.className = 'change-legend-title'
  title.textContent = opts.title ?? (opts.mode === 'percent'
    ? t('research.change.legendPercent')
    : opts.mode === 'sigma'
      ? t('research.change.legendSigma')
      : t('research.change.legendDifference', { units: opts.units ?? '' }))
  if (opts.frameLabel && !opts.title) title.textContent += ` · ${t('research.change.since', { frame: opts.frameLabel })}`
  const bar = document.createElement('div')
  bar.className = 'change-legend-bar'
  bar.style.background = changeGradient(opts.colours)
  bar.setAttribute('aria-hidden', 'true')
  const ticks = document.createElement('div')
  ticks.className = 'change-legend-ticks'
  for (const v of [opts.min ?? -opts.limit, 0, opts.limit]) {
    const s = document.createElement('span')
    s.textContent = changeTick(v, opts.mode, opts.units)
    ticks.appendChild(s)
  }
  wrap.append(title, bar, ticks)
  return wrap
}
